import { noop } from "lodash-es";
import { defineStore } from "pinia";

import { windowFromRelative } from "@/math/coordinate-transform";
import { relativeFromViewport, scrollTo } from "@/math/coordinate-utils";
import type { ZoomedRectangle } from "@/model/board";
import type { RelativeCoordinate, ViewportCoordinate } from "@/model/coordinates";

interface Scroller {
  scroll: (rect: ZoomedRectangle) => void;
}

export const useZoomStore = defineStore("zoom", {
  state: () => ({
    zooming: false,
    dropdownOpen: false,
    useZoomLayer: true,
    factor: 1, // only updated at the end of zooming
    dynamicFactor: 1, // also updated during zooming
    scroller: { scroll: noop } as Scroller,
  }),
  getters: {
    zoomingWithLayer: (state) => state.zooming && state.useZoomLayer,
  },
  actions: {
    setDynamicZoomFactor(factor: number, exact = false) {
      this.dynamicFactor = exact ? factor : round(factor);
    },
    setZoomFactor(factor: number) {
      this.setDynamicZoomFactor(factor);
      this.updateZoomFactor();
    },
    updateZoomFactor() {
      this.factor = round(this.dynamicFactor);
    },
    startZoom(scroller: Scroller) {
      this.scroller = scroller;
      this.zooming = true;
    },
    endZoom() {
      this.zooming = false;
    },
    setDropdownOpen(open: boolean) {
      this.dropdownOpen = open;
    },
  },
});

// scroll so that the viewport coordinate matches the initial relative coordinate
export class TargetScroller {
  viewport: ViewportCoordinate;
  relative: RelativeCoordinate;
  constructor(target: ViewportCoordinate) {
    this.viewport = target;
    this.relative = relativeFromViewport(target);
  }

  scroll(rect: ZoomedRectangle) {
    const windowCoord = windowFromRelative(this.relative, { board: rect });
    scrollTo(windowCoord, this.viewport);
  }
}

function round(zoom: number) {
  return Math.round(100 * zoom) / 100;
}
